import { Activity, memo, useRef, useState } from 'react';

import { useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import Table from '@layouts/layout-table/layout-table';
import TableHead from '@layouts/layout-table/layout-table-head';
import TableRow from '@layouts/layout-table/layout-table-row';
import TableBody from '@layouts/layout-table/layout-table-body';
import CrewContentTableHeader from './crew-content-table-header';
import CrewContentTableRow from './crew-content-table-row';
import useSortedAndPagedCrewList from './helpers/use-sorted-and-paged-crew-list';
import useComputedColorVariableValue from '@/utils/colors/use-computed-color-variable-value';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';

const MAX_ROWS_COLLAPSED = 5;

const CrewContent = memo(() => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const activeChannel = useAppSelector(getActiveChannel);

  const [showMore, setShowMore] = useState(false);

  const { sortedCrew } = useSortedAndPagedCrewList();
  const sortedCrewCount = sortedCrew?.length || 0;

  const computedBgHex = useComputedColorVariableValue({ variable: 'var(--BG)' });

  if (!sortedCrew?.length || !activeChannel) return null;
  return (
    <div>
      <SlideDownCard
        // 64px is each row height, 36px is the table header height
        height={showMore ? undefined : `calc(calc(64px * ${Math.min(MAX_ROWS_COLLAPSED, sortedCrewCount)}) + 36px)`}
        open
        autoGrow={showMore}
        wrapperClassname="overflow-hidden"
      >
        <Table ignoreHeaderRowSpacing>
          <TableHead border={0} bgcolor={computedBgHex} className="position-sticky top-0 z-2 height-auto">
            <TableRow border={0} className="height-30">
              <CrewContentTableHeader />
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedCrew?.map((crewMember, idx) => {
              const isInert = !showMore && idx > MAX_ROWS_COLLAPSED - 1;
              if (!crewMember.id) return null;
              return (
                <CrewContentTableRow inert={isInert} scrollRef={scrollRef} key={crewMember.id} member={crewMember} />
              );
            })}
          </TableBody>
        </Table>
      </SlideDownCard>

      <Activity mode={sortedCrewCount > MAX_ROWS_COLLAPSED ? 'visible' : 'hidden'}>
        <div className={`flex jc-center ai-center ${showMore ? 'margin-t-6' : ''}`}>
          <Button designSize="small" design="LocalBttnSecHalfTransp" onClick={() => setShowMore((prev) => !prev)}>
            <Text className="L2">Show {showMore ? 'Less' : 'More'}</Text>
          </Button>
        </div>
      </Activity>
    </div>
  );
});

CrewContent.displayName = 'CrewContent';

export default CrewContent;
